import { HarvestType } from '@/common/constants/harvest.constants';
import { PartiesEnum } from '@/common/constants/parties.constants';
import { InputControl } from '@/components/controls/input-control';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { usePartyStore } from '@/features/parties/store/usePartyStore';
import { cn, determineClassByInterval } from '@/lib/utils';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDetermineClass } from '../../hooks/useDetermineClass';
import { IAnalysis } from '../../models';
import { useLoadStore } from '../../store';
import { CertificateNumber } from './certificate-number/CertificateNumber';

interface IProps {
  cancel: () => void;
  onSubmit: (data: any) => void;
  setOpenDialog: (a: boolean) => void;
  openDialog: boolean;
  harvest_type?: string;
  crop_type_id?: string;
  analysis: IAnalysis | undefined;
  crop_generation_type: PartiesEnum;
}

export interface AnalysisFormRef {
  submit: () => void;
}

export const AnalysisForm = forwardRef<AnalysisFormRef, IProps>(
  (
    {
      openDialog,
      cancel,
      onSubmit,
      setOpenDialog,
      harvest_type,
      crop_type_id,
      analysis,
      crop_generation_type,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const form = useFormContext();
    const { control, handleSubmit, formState, watch, setValue, clearErrors } =
      form;

    const {
      cropTypes,
      classList,
      industrialGrades,
      fetchCropTypesById,
      partiesSelectGeneral,
      fetchPartiesSelect,
    } = usePartyStore();
    const { load } = useLoadStore();

    const watchedPollution = useWatch({
      control: form.control,
      name: 'pollution',
    });
    const watchedHumidity = useWatch({
      control: form.control,
      name: 'humidity',
    });
    const industrialGrade = useWatch({
      control: form.control,
      name: 'industrial_grade',
    });
    const watchedClass = useWatch({
      control: form.control,
      name: 'class',
    });
    const watchedParty = useWatch({ control, name: 'party_id' });

    const { filterClassList } = useDetermineClass({
      watchedPollution,
      watchedHumidity,
      industrialGrade,
      clearErrors,
      classList,
      setValue,
      harvest_type,
    });

    const [openDalolatDialog, setOpenDalolatDialog] = useState(false);
    const [pendingGrade, setPendingGrade] = useState<number | null>(null);
    const [confirmedParty, setConfirmedParty] = useState<string | null>(null);
    const [partyDisabled, setPartyDisabled] = useState(true);
    const [selectedPartyData, setSelectedPartyData] = useState<any>(null);

    const originalIndustrialGrade = useRef<number | null>(null);
    const isGradeChanged = useRef<boolean>(false);

    useEffect(() => {
      if (
        analysis?.industrial_grade &&
        originalIndustrialGrade.current === null
      ) {
        setValue('industrial_grade', analysis.industrial_grade);
        originalIndustrialGrade.current = Number(analysis.industrial_grade);
      }
    }, [analysis, setValue]);

    const party_list = useMemo(() => {
      const currentIG = Number(industrialGrade);
      const originalIG = originalIndustrialGrade.current;

      return partiesSelectGeneral
        ?.filter((p) => {
          // 1. Cluster va Generation bo'yicha tekshirish
          if (
            p?.generation === crop_generation_type &&
            p?.cluster_id === load?.cluster_id
          ) {
            if (p?.generation === PartiesEnum.SEED) {
              if (p?.crop_generation_id !== load?.crop_generation_id)
                return false;
              if (p?.storage_type !== load?.storage_type) return false;
            }
          } else {
            return false;
          }

          if (isGradeChanged.current && selectedPartyData) {
            if (p.id === selectedPartyData.id) return true;
          }
          let allowedGrades: number[] = [];
          if (originalIG !== null && currentIG > originalIG) {
            allowedGrades = [1, 2, 3, 4, 5];
          } else {
            if (currentIG === 1) {
              allowedGrades = [1, 2];
            } else if (currentIG === 2) {
              allowedGrades = [2, 3];
            } else {
              allowedGrades = [currentIG, currentIG + 1];
            }
          }

          const matchesIndustrialGrade = allowedGrades.includes(
            Number(p?.industrial_grade),
          );

          const finalClass = determineClassByInterval(
            watchedPollution,
            Number(p?.industrial_grade),
            harvest_type,
          );

          let matchesClass = true;
          if (finalClass) {
            const partyClassStr = String(p?.class);
            const finalClassStr = String(finalClass);

            if (
              finalClassStr === '2' &&
              partyClassStr === '1' &&
              Number(p?.industrial_grade) === 1 &&
              (harvest_type === HarvestType.COMBINE ||
                harvest_type === HarvestType.GROUND_HARVESTED)
            ) {
              matchesClass = true;
            } else {
              matchesClass = partyClassStr === finalClassStr;
            }
          }

          return matchesIndustrialGrade && matchesClass;
        })
        ?.sort((a, b) => {
          const gradeA = Number(a?.industrial_grade ?? 0);
          const gradeB = Number(b?.industrial_grade ?? 0);
          return gradeA - gradeB;
        })
        ?.map((p) => {
          const finalClass = determineClassByInterval(
            watchedPollution,
            Number(p?.industrial_grade),
            harvest_type,
          );

          // Labelda partiyaning asl sanoat navi va hisoblangan dinamik sinfi ko'rinadi
          return {
            label: `${p?.place_number} - ${p?.party_number} - (Sanoat navi:${p?.industrial_grade} sinfi:${finalClass || p?.class})`,
            value: p?.id,
            ...p,
          };
        });
    }, [
      partiesSelectGeneral,
      industrialGrade,
      watchedClass,
      watchedPollution,
      harvest_type,
      crop_generation_type,
      load?.cluster_id,
      load?.crop_generation_id,
      load?.storage_type,
      load?.harvest_type,
      selectedPartyData,
    ]);

    useEffect(() => {
      if (watchedClass && !confirmedParty) {
        setPartyDisabled(false);
      }
    }, [watchedClass, confirmedParty]);

    useEffect(() => {
      if (watchedParty && !isGradeChanged.current) {
        const selected = party_list?.find((p) => p.id === watchedParty);
        if (
          selected &&
          Number(selected.industrial_grade) > Number(industrialGrade)
        ) {
          setPendingGrade(Number(selected.industrial_grade));
          setSelectedPartyData(selected);
          setOpenDalolatDialog(true);
          setPartyDisabled(false);
        } else {
          setConfirmedParty(watchedParty);
          setPartyDisabled(false);
        }
      }
    }, [watchedParty, party_list, industrialGrade, setValue]);

    useEffect(() => {
      if (crop_type_id) {
        fetchCropTypesById(crop_type_id);
        fetchPartiesSelect(crop_type_id, load?.harvest_type);
      }
    }, [crop_type_id, load?.harvest_type]);

    useEffect(() => {
      form.setValue(
        'analysis_crop_type_id',
        cropTypes?.find((el) => el.value == crop_type_id)?.value,
      );
    }, [crop_type_id, cropTypes]);

    const handleConfirmDalolatnoma = () => {
      if (pendingGrade && watchedParty && selectedPartyData) {
        setValue('industrial_grade', pendingGrade);
        setConfirmedParty(watchedParty);
        setPartyDisabled(true);
        isGradeChanged.current = true;
      }
      setOpenDalolatDialog(false);
      setPendingGrade(null);
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      },
    }));

    return (
      <FormProvider {...form}>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={cn('flex flex-col items-start space-y-5')}
          >
            <div className="grid grid-cols-5 gap-4 w-full">
              <SelectControl
                name="analysis_crop_type_id"
                label={t('selection.type')}
                options={cropTypes}
                control={control}
                placeholder={t('choose.data')}
                className="col-span-1"
                disabled
              />
              <InputControl
                control={control}
                name="pollution"
                label={t('pollution')}
                className="col-span-1"
                isNumber
                max={22}
              />
              <InputControl
                control={control}
                name="humidity"
                label={t('humidity')}
                className="col-span-1"
                isNumber
              />
              <SelectControl
                name="industrial_grade"
                label={t('industry.type')}
                options={industrialGrades}
                control={control}
                placeholder={t('choose.data')}
                className="col-span-1"
                disabled
              />
              <div className="col-span-1">
                <SelectControl
                  name="class"
                  label={t('class')}
                  options={filterClassList}
                  control={control}
                  placeholder={t('choose.data')}
                  className=""
                  disabled
                />
              </div>

              <SelectControl
                name="party_id"
                label={t('party')}
                options={party_list}
                control={control}
                placeholder={t('choose.data')}
                className="col-span-1"
                disabled={partyDisabled}
                disableSort
              />
            </div>

            <div className="flex-h-center space-x-4">
              <Button type="button" variant="outline" onClick={cancel}>
                {t('cancel')}
              </Button>
              <Button
                type="button"
                disabled={!formState.isValid}
                onClick={() => setOpenDialog(!openDialog)}
              >
                {t('save')}
              </Button>
            </div>
          </form>
        </Form>

        <CertificateNumber
          openDalolatDialog={openDalolatDialog}
          setOpenDalolatDialog={setOpenDalolatDialog}
          handleConfirmDalolatnoma={handleConfirmDalolatnoma}
        />
      </FormProvider>
    );
  },
);

AnalysisForm.displayName = 'AnalysisForm';
